import serviciosV2Client from "../../../helpers/serviciosV2Client"

const OPERACIONAL = "analytics/metodos-pago-operacional"
const FACTURAS = "analytics/facturas-metodos-pago"
const PAGOS_DESGLOSE = "analytics/pagos/desglose"

const cleanParams = obj =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  )

/**
 * Vista operacional agregada por sucursal × método × día.
 * Con incluir=todos trae también: kpis, top_bottom, comparativo, distribucion, alertas.
 *
 * Acepta dos formas de período:
 *   - year + month  → mes completo
 *   - fecha_inicio + fecha_fin → rango libre
 * (cleanParams descarta lo que no venga).
 *
 * @param {object} opts
 * @param {string} opts.nitemisor
 * @param {number} [opts.year]
 * @param {number} [opts.month]
 * @param {string} [opts.fecha_inicio] YYYY-MM-DD
 * @param {string} [opts.fecha_fin]    YYYY-MM-DD
 * @param {string} [opts.sucursal]
 * @param {string} [opts.tipo_metodo]
 * @param {"fecha"|"fechageneracion"} [opts.campo_fecha]
 * @param {string} [opts.incluir]
 */
export const getOperacional = ({
  nitemisor, year, month, fecha_inicio, fecha_fin,
  sucursal, tipo_metodo, campo_fecha, incluir = "todos",
}) =>
  serviciosV2Client.get(OPERACIONAL, {
    params: cleanParams({
      nitemisor, year, month, fecha_inicio, fecha_fin,
      sucursal, tipo_metodo, campo_fecha, incluir,
    }),
  })

/**
 * ⭐ Analítica de Pagos — desglose parametrizado (una fuente × una dimensión).
 * Cubre ambas fuentes y todas las agrupaciones; recomendado para tableros
 * por sucursal / día / medio.
 *
 *   fuente="metodos_pago" → valor ajustado (reglas del POS)
 *   fuente="datafono"     → valor real del terminal (dato limpio)
 *
 * Cada fila trae sus columnas de dimensión según agrupar_por, más las métricas
 * transacciones, valor (neto: NC restan) y ticket_promedio.
 *
 * @param {object} opts
 * @param {string} opts.nitemisor                       (obligatorio)
 * @param {"dia"|"sucursal"|"sucursal_dia"|"medio"|"sucursal_medio"|"sucursal_medio_dia"} opts.agrupar_por (obligatorio)
 * @param {"metodos_pago"|"datafono"} [opts.fuente="metodos_pago"]
 * @param {string} [opts.fecha_inicio] YYYY-MM-DD
 * @param {string} [opts.fecha_fin]    YYYY-MM-DD
 * @param {string} [opts.sucursal]     sucursal exacta
 * @param {string} [opts.medio]        medio/método (coincidencia parcial, ILIKE)
 * @param {"fecha"|"fechageneracion"|"fecha_pago"} [opts.campo_fecha="fecha"]
 * @returns {Promise} { success, data:[...], resumen, params }
 */
export const getPagosDesglose = ({
  nitemisor, agrupar_por, fuente, fecha_inicio, fecha_fin,
  sucursal, medio, campo_fecha,
}) =>
  serviciosV2Client.get(PAGOS_DESGLOSE, {
    params: cleanParams({
      nitemisor, agrupar_por, fuente, fecha_inicio, fecha_fin,
      sucursal, medio, campo_fecha,
    }),
  })

/**
 * Vista factura-céntrica con los ajustes de las 3 reglas del POS.
 * Con incluir=todos trae también: combinaciones, por_sucursal, inconsistencias.
 *
 * @param {object} opts
 * @param {string} opts.nitemisor
 * @param {string} opts.fecha_inicio
 * @param {string} opts.fecha_fin
 * @param {string} [opts.sucursal]
 * @param {string} [opts.tipo_metodo]
 * @param {"fecha"|"fechageneracion"} [opts.campo_fecha]
 * @param {number} [opts.limit]
 * @param {number} [opts.offset]
 * @param {string} [opts.incluir]
 */
export const getFacturas = ({
  nitemisor, fecha_inicio, fecha_fin, sucursal, tipo_metodo, campo_fecha,
  limit = 1000, offset = 0, incluir,
}) =>
  serviciosV2Client.get(FACTURAS, {
    params: cleanParams({
      nitemisor, fecha_inicio, fecha_fin, sucursal, tipo_metodo, campo_fecha,
      limit, offset, incluir,
    }),
  })

/**
 * Descarga el export completo de facturas (con todos los métodos) como
 * archivo binario directo del back (CSV o XLSX). El back devuelve el
 * archivo con Content-Disposition; respetamos ese filename.
 *
 * @param {object} opts
 * @param {"xlsx"|"csv"} opts.formato
 * @returns {Promise<{ totalRows, totalSheets, filename }>}
 */
export const descargarFacturasExport = async ({
  nitemisor, fecha_inicio, fecha_fin, sucursal, tipo_metodo, campo_fecha,
  formato = "xlsx",
}) => {
  let response
  try {
    response = await serviciosV2Client.get(`export/facturas-metodos-pago.${formato}`, {
      params: cleanParams({ nitemisor, fecha_inicio, fecha_fin, sucursal, tipo_metodo, campo_fecha }),
      responseType: "blob",
      timeout: 600000, // 10 min — el Excel de 100k filas tarda ~11s
    })
  } catch (err) {
    // Si el back devuelve 400 con JSON, axios devuelve el blob en el error
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.error || json.message || "Error al exportar.")
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message !== "Unexpected token < in JSON at position 0") {
          throw parseErr
        }
      }
    }
    throw err
  }

  // Parsear filename desde Content-Disposition
  const cd = response.headers["content-disposition"] || response.headers["Content-Disposition"] || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  const filename = match
    ? decodeURIComponent(match[1])
    : `facturas_metodos_pago_${nitemisor}_${fecha_inicio}_${fecha_fin}.${formato}`

  const contentType = response.headers["content-type"] ||
    (formato === "xlsx"
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "text/csv;charset=utf-8;")

  const blob = new Blob([response.data], { type: contentType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    totalRows: Number(response.headers["x-total-rows"]) || null,
    totalSheets: Number(response.headers["x-total-sheets"]) || null,
    filename,
  }
}

/* ─────────────────────────────────────────────────────────────
 *  Datáfono — resumen y conciliación (§3.2 y §4)
 * ───────────────────────────────────────────────────────────── */

/**
 * Headline ejecutivo del datáfono: totales del rango + desglose por día,
 * sucursal y medio en una sola llamada. Solo devuelve datos para emisores
 * que tienen el servicio de datáfono.
 *
 * @param {object} opts
 * @param {string} opts.nitemisor               (obligatorio)
 * @param {string} [opts.fecha_inicio] YYYY-MM-DD
 * @param {string} [opts.fecha_fin]    YYYY-MM-DD
 * @param {string} [opts.sucursal]
 * @param {string} [opts.medio]
 * @param {"fecha"|"fecha_pago"} [opts.campo_fecha="fecha"]
 * @returns {Promise} { success, resumen:{ total_transacciones, total_valor, ... por_dia, por_sucursal, por_medio }, params }
 */
export const getDatafonoResumen = ({
  nitemisor, fecha_inicio, fecha_fin, sucursal, medio, campo_fecha,
}) =>
  serviciosV2Client.get("analytics/datafono/resumen", {
    params: cleanParams({ nitemisor, fecha_inicio, fecha_fin, sucursal, medio, campo_fecha }),
  })

/**
 * Conciliación datáfono (confirmado) vs POS (reportado) por factura/cufe.
 * Paginado. Cada fila trae un `estado`: CUADRA | DIFERENCIA | SIN_REPORTE.
 *
 * @param {object} opts
 * @param {string} opts.nitemisor
 * @param {string} [opts.fecha_inicio] YYYY-MM-DD
 * @param {string} [opts.fecha_fin]    YYYY-MM-DD
 * @param {string} [opts.sucursal]
 * @param {"CUADRA"|"DIFERENCIA"|"SIN_REPORTE"} [opts.estado]
 * @param {string} [opts.medio]
 * @param {number} [opts.page=1]
 * @param {number} [opts.page_size=50]  (máx 1000)
 * @returns {Promise} { success, data:[...], resumen:{...}, pagination:{...} }
 */
export const getDatafonoConciliacion = ({
  nitemisor, fecha_inicio, fecha_fin, sucursal, estado, medio, page, page_size,
}) =>
  serviciosV2Client.get("analytics/datafono/conciliacion", {
    params: cleanParams({
      nitemisor, fecha_inicio, fecha_fin, sucursal, estado, medio, page, page_size,
    }),
  })

/* ─────────────────────────────────────────────────────────────
 *  Descargas (blob) — helper genérico + exports nuevos
 * ───────────────────────────────────────────────────────────── */

/**
 * Descarga genérica de un export (CSV/XLSX) del back respetando el
 * Content-Disposition. Si el back responde un JSON de error dentro del blob,
 * lo detecta y lanza el mensaje. Devuelve metadata de headers.
 */
const descargarExport = async (path, params, fallbackName) => {
  let response
  try {
    response = await serviciosV2Client.get(path, {
      params: cleanParams(params),
      responseType: "blob",
      timeout: 600000,
    })
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.error || json.message || "Error al exportar.")
      } catch (parseErr) {
        if (parseErr instanceof Error && parseErr.message.startsWith("Error al exportar")) throw parseErr
      }
    }
    throw err
  }

  const cd = response.headers["content-disposition"] || response.headers["Content-Disposition"] || ""
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  const filename = match ? decodeURIComponent(match[1]) : fallbackName

  const isXlsx = path.endsWith(".xlsx")
  const contentType = response.headers["content-type"] ||
    (isXlsx
      ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      : "text/csv;charset=utf-8;")

  const blob = new Blob([response.data], { type: contentType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    totalRows: Number(response.headers["x-total-rows"]) || null,
    totalSheets: Number(response.headers["x-total-sheets"]) || null,
    filename,
  }
}

/**
 * Descarga del desglose parametrizado (§3.3). Mismos filtros que
 * getPagosDesglose. formato: "csv" | "xlsx".
 */
export const descargarPagosDesgloseExport = ({
  nitemisor, agrupar_por, fuente, fecha_inicio, fecha_fin,
  sucursal, medio, campo_fecha, formato = "xlsx",
}) =>
  descargarExport(
    `export/pagos-desglose.${formato}`,
    { nitemisor, agrupar_por, fuente, fecha_inicio, fecha_fin, sucursal, medio, campo_fecha },
    `pagos_desglose_${fuente || "metodos_pago"}_${agrupar_por}_${nitemisor}.${formato}`,
  )

/**
 * Descarga de la conciliación datáfono vs POS (§4.2). Mismos filtros que
 * getDatafonoConciliacion (sin paginación). formato: "csv" | "xlsx".
 */
export const descargarDatafonoConciliacionExport = ({
  nitemisor, fecha_inicio, fecha_fin, sucursal, estado, medio, formato = "xlsx",
}) =>
  descargarExport(
    `export/datafono-conciliacion.${formato}`,
    { nitemisor, fecha_inicio, fecha_fin, sucursal, estado, medio },
    `datafono_conciliacion_${nitemisor}_${fecha_inicio || ""}_${fecha_fin || ""}.${formato}`,
  )
